
import { useEffect, useRef } from "react";
import { ExternalLink, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import aiEcommerceImg from "@/assets/project-ai-ecommerce.jpg";
import netsuiteImg from "@/assets/project-netsuite.jpg";
import powerbiImg from "@/assets/project-powerbi.jpg";
import celigoImg from "@/assets/project-celigo.jpg";
import aiWorkflowImg from "@/assets/project-ai-workflow.jpg";
import executiveAiImg from "@/assets/project-executive-ai.jpg";
import aiAgentsImg from "@/assets/project-ai-agents.jpg";

export const Portfolio = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll, .animate-on-scroll-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const projects = [
    {
      title: "Manufacturing & ERP Transformation",
      description: "End-to-end manufacturing operations overhaul with comprehensive ERP implementation, production planning, quality management, and real-time visibility.",
      tech: ["ERP", "Manufacturing", "MRP", "QMS"],
      image: netsuiteImg,
      link: "/contact?project=manufacturing-erp",
      metrics: { label: "Scope", value: "Full Stack" },
      category: "ERP & Manufacturing",
      featured: true
    },
    {
      title: "Global Supply Chain Platform",
      description: "Unified supply chain connecting warehouses, vendors, and logistics with EDI connectivity.",
      tech: ["WMS", "Inventory", "EDI"],
      image: celigoImg,
      link: "/contact?project=supply-chain",
      metrics: { label: "Regions", value: "Multi-Region" },
      category: "Supply Chain",
      featured: false
    },
    {
      title: "Unified Commerce Experience",
      description: "Omnichannel commerce platform with CRM integration and real-time inventory synchronization.",
      tech: ["eCommerce", "CRM", "Middleware"],
      image: aiEcommerceImg,
      link: "/contact?project=unified-commerce",
      metrics: { label: "Channels", value: "Omnichannel" },
      category: "Commerce",
      featured: false
    },
    {
      title: "Executive Intelligence Suite",
      description: "Real-time executive dashboards with AI-powered insights, sales forecasting, and digital asset management across the enterprise.",
      tech: ["Analytics", "CRM", "DAM", "AI"],
      image: executiveAiImg,
      link: "/contact?project=intelligence-suite",
      metrics: { label: "Impact", value: "Data-Driven" },
      category: "Intelligence",
      featured: true
    },
    {
      title: "Intelligent Automation Hub",
      description: "AI-powered operations center with workflow automation and intelligent agents.",
      tech: ["AI", "Middleware", "DDMRP"],
      image: aiAgentsImg,
      link: "/contact?project=automation-hub",
      metrics: { label: "Systems", value: "Cross-System" },
      category: "Automation",
      featured: false
    },
    {
      title: "Financial Operations Center",
      description: "Comprehensive financial management with GL/AP/AR automation and audit-ready reporting.",
      tech: ["Financials", "ERP", "Compliance"],
      image: powerbiImg,
      link: "/contact?project=financial-ops",
      metrics: { label: "Status", value: "Audit-Ready" },
      category: "Finance",
      featured: false
    }
  ];

  const featuredProjects = projects.filter(p => p.featured);
  const standardProjects = projects.filter(p => !p.featured);

  return (
    <section ref={sectionRef} id="portfolio" className="py-32 relative overflow-hidden noise-overlay">
      {/* Background */}
      <div className="absolute inset-0 bg-zinc-950" />
      <div className="absolute inset-0 dot-pattern opacity-20" />

      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-1/4 left-0 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[120px]" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-20">
          <div className="animate-on-scroll">
            <span className="label-mono text-amber-400 tracking-widest">
              Proven Results
            </span>
          </div>

          <h2 className="mt-6 display-lg text-white animate-on-scroll stagger-1">
            Success
            <span className="text-gradient-gold"> Stories</span>
          </h2>

          <p className="mt-6 text-xl text-zinc-400 leading-relaxed animate-on-scroll stagger-2">
            Real projects delivering measurable impact across industries.
          </p>
        </div>

        {/* Featured Projects - Large Cards */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {featuredProjects.map((project, index) => (
            <div
              key={index}
              className={`animate-on-scroll-scale stagger-${index + 1} group relative rounded-3xl overflow-hidden hover-lift`}
            >
              {/* Image */}
              <div className="aspect-[16/10] relative overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

                {/* Category Badge */}
                <div className="absolute top-6 left-6">
                  <span className="px-4 py-2 bg-amber-500/90 text-black text-sm font-semibold rounded-full">
                    {project.category}
                  </span>
                </div>

                {/* Link Button */}
                <div className="absolute top-6 right-6">
                  <button
                    onClick={() => window.location.href = project.link}
                    className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all duration-300 group-hover:scale-110"
                    aria-label={`View ${project.title} case study`}
                  >
                    <ArrowUpRight className="w-5 h-5" />
                  </button>
                </div>

                {/* Content Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  {/* Metrics Badge */}
                  <div className="mb-4">
                    <div className="inline-flex items-center gap-2 glass-panel rounded-lg px-4 py-2">
                      <span className="text-xs text-zinc-400">{project.metrics.label}</span>
                      <span className="text-sm font-bold text-amber-400">{project.metrics.value}</span>
                    </div>
                  </div>

                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 group-hover:text-gradient-gold transition-all duration-300">
                    {project.title}
                  </h3>

                  <p className="text-zinc-300 mb-4 leading-relaxed max-w-xl">
                    {project.description}
                  </p>

                  {/* Tech Tags */}
                  <div className="flex flex-wrap gap-2">
                    {project.tech.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-3 py-1 text-xs font-medium rounded-full bg-white/10 text-white/80 border border-white/10"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Standard Projects - Smaller Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {standardProjects.map((project, index) => (
            <div
              key={index}
              className={`animate-on-scroll stagger-${index + 3} group card-editorial overflow-hidden hover:border-amber-500/30`}
            >
              {/* Image */}
              <div className="aspect-[4/3] relative overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent" />

                {/* Category */}
                <div className="absolute top-3 left-3">
                  <span className="px-2 py-1 bg-zinc-900/80 backdrop-blur-sm text-xs font-medium text-amber-400 rounded">
                    {project.category}
                  </span>
                </div>

                {/* Metric */}
                <div className="absolute bottom-3 left-3">
                  <div className="text-xs text-zinc-400">{project.metrics.label}</div>
                  <div className="text-sm font-bold text-white">{project.metrics.value}</div>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h4 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors line-clamp-1">
                  {project.title}
                </h4>

                <p className="text-sm text-zinc-500 mb-4 line-clamp-2">
                  {project.description}
                </p>

                {/* Tech Tags */}
                <div className="flex flex-wrap gap-1">
                  {project.tech.slice(0, 3).map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-2 py-0.5 text-xs rounded bg-zinc-800 text-zinc-400"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center animate-on-scroll">
          <div className="inline-block">
            <p className="text-zinc-400 mb-6 text-lg">Have a similar project in mind?</p>
            <Button
              size="lg"
              className="btn-primary h-14 px-10 rounded-full text-lg group"
              onClick={() => window.location.href = '/contact'}
            >
              Discuss Your Project
              <ArrowUpRight className="ml-2 w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
